import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, setToken } from './api.js'
import {
  Banner,
  CategoryRow,
  PlanGrid,
  Spinner,
  Sparkline,
  SurplusHeadline,
  TargetEditor,
  TransactionRow,
  isTargetable,
} from './components.jsx'
import { fmtMoney, fmtMonth, stateMeta } from './format.js'

/**
 * A pure viewer. Every number rendered here was computed by compute_state and
 * read from one snapshot, so the phone and Claude can never disagree about
 * what the month looks like. The only thing this app writes is a flag — an
 * observation that something looks wrong, which tomorrow's audit turns into a
 * rule.
 */
export default function App() {
  const [view, setView] = useState({ name: 'home' })
  const [state, setState] = useState(null)
  const [month, setMonth] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  // Planning ("target mode"): off by default, since dragging a cap is
  // otherwise one accidental tap away on every category. While on, edits
  // stage here rather than writing through immediately, so trying out
  // numbers to see whether they balance against take-home doesn't touch
  // the real config until Save.
  const [editMode, setEditMode] = useState(false)
  const [pendingCaps, setPendingCaps] = useState({})
  const [savingTargets, setSavingTargets] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const load = useCallback(async (targetMonth) => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.state(targetMonth)
      setState(data)
      setMonth(data.month)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onChangeCap = useCallback((categoryId, value) => {
    setPendingCaps((prev) => ({ ...prev, [categoryId]: value }))
  }, [])

  function discardTargets() {
    setPendingCaps({})
    setSaveError(null)
  }

  async function saveTargets() {
    setSavingTargets(true)
    setSaveError(null)
    try {
      // One at a time, not Promise.all: the backend writes categories.json
      // as a single read-modify-write over the whole file (see its
      // docstring), so parallel PATCHes race — each reads a snapshot from
      // before the others' writes land, and whichever write finishes last
      // silently clobbers the rest back to their old values.
      for (const [categoryId, cap] of Object.entries(pendingCaps)) {
        await api.setCap(categoryId, cap)
      }
      setPendingCaps({})
      // Caps alone don't move which category a transaction belongs to, so
      // this reruns compute_state (not categorize) — the new numbers show
      // up now instead of at the next scheduled pipeline run.
      await api.recompute()
      await load(month)
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSavingTargets(false)
    }
  }

  if (error instanceof ApiError && error.status === 401) {
    return <SignIn onSignedIn={() => load(month)} />
  }

  const pendingCount = Object.keys(pendingCaps).length

  return (
    <div className={`mx-auto min-h-screen max-w-lg sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl px-4 pb-24 ${editMode ? 'pt-20' : 'pt-6'}`}>
      {editMode && state && (
        <TargetBalanceBar household={state.household} categories={state.categories} pendingCaps={pendingCaps} />
      )}
      <Breadcrumb view={view} onHome={() => setView({ name: 'home' })} />
      {view.name === 'home' && (
        <Home
          state={state}
          loading={loading}
          error={error}
          month={month}
          onMonth={(m) => load(m)}
          onOpen={(id, label) => setView({ name: 'category', id, label })}
          onAudit={() => setView({ name: 'audit' })}
          onEvents={() => setView({ name: 'events' })}
          onOpenEvent={(name) => setView({ name: 'event', event: name })}
          onLargePurchases={() => setView({ name: 'large-purchases' })}
          onReload={() => load(month)}
          editMode={editMode}
          onToggleEditMode={() => setEditMode((v) => !v)}
          pendingCaps={pendingCaps}
          onChangeCap={onChangeCap}
        />
      )}
      {view.name === 'category' && (
        <Category
          categoryId={view.id}
          label={view.label}
          month={month}
          onBack={() => setView({ name: 'home' })}
          onFlagged={() => load(month)}
          editMode={editMode}
          pendingCap={pendingCaps[view.id]}
          onChangeCap={onChangeCap}
        />
      )}
      {view.name === 'audit' && <AuditLog onBack={() => setView({ name: 'home' })} />}
      {view.name === 'events' && (
        <Events
          onBack={() => setView({ name: 'home' })}
          onOpen={(name) => setView({ name: 'event', event: name })}
        />
      )}
      {view.name === 'event' && (
        <EventDetail event={view.event} onBack={() => setView({ name: 'events' })} />
      )}
      {view.name === 'large-purchases' && (
        <LargePurchases
          onBack={() => setView({ name: 'home' })}
          onReviewed={() => load(month)}
        />
      )}

      {pendingCount > 0 && (
        <SaveBar
          count={pendingCount}
          saving={savingTargets}
          error={saveError}
          onSave={saveTargets}
          onDiscard={discardTargets}
        />
      )}
    </div>
  )
}

function SaveBar({ count, saving, error, onSave, onDiscard }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 backdrop-blur">
      <div className="mx-auto max-w-lg sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl px-4 py-3">
        {error && <div className="mb-2 text-xs text-over">{error}</div>}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-neutral-600">
            {count} target{count === 1 ? '' : 's'} changed
          </span>
          <div className="flex gap-2">
            <button
              onClick={onDiscard}
              disabled={saving}
              className="rounded-full border border-neutral-200 px-3 py-1.5 text-sm
                         text-neutral-500 disabled:opacity-40"
            >
              Discard
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="rounded-full bg-brand-600 px-3.5 py-1.5 text-sm font-medium text-white
                         shadow-sm disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Always visible, always one tap from home — the app has no other persistent
 * chrome, so this is the only thing telling you where you are.
 */
function Breadcrumb({ view, onHome }) {
  const current =
    view.name === 'category' ? view.label ?? view.id?.replace(/_/g, ' ')
    : view.name === 'audit' ? 'Audit log'
    : view.name === 'events' ? 'Events'
    : view.name === 'event' ? view.event
    : view.name === 'large-purchases' ? 'Large purchases'
    : null

  return (
    <div className="mb-4 flex items-center gap-1.5 text-sm">
      <button
        onClick={onHome}
        className={current ? 'font-semibold text-brand-600' : 'font-semibold text-neutral-900'}
      >
        Finance
      </button>
      {current && (
        <>
          <span className="text-neutral-300">/</span>
          <span className="truncate text-neutral-500">{current}</span>
        </>
      )}
    </div>
  )
}

/**
 * Google Identity Services (the plain <script> tag in index.html, not a
 * library) issues a signed ID token client-side; that token is stored and
 * sent as the bearer token exactly like the old pasted one was — the API
 * verifies it against Google's keys and an email allowlist instead of a
 * static value. Login expires with the token (~1hr); the existing 401 ->
 * show this screen flow already handles that with no extra code.
 */
function SignIn({ onSignedIn }) {
  const buttonRef = useRef(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    function handleCredential(response) {
      setToken(response.credential)
      onSignedIn()
    }

    function renderButton() {
      if (!window.google?.accounts?.id || !buttonRef.current) return
      const clientId = import.meta.env?.VITE_GOOGLE_CLIENT_ID
      if (!clientId) {
        setError('Sign-in is not configured (missing Google client id).')
        return
      }
      window.google.accounts.id.initialize({ client_id: clientId, callback: handleCredential })
      window.google.accounts.id.renderButton(buttonRef.current, { theme: 'outline', size: 'large' })
    }

    if (window.google?.accounts?.id) {
      renderButton()
      return
    }
    const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]')
    script?.addEventListener('load', renderButton)
    return () => script?.removeEventListener('load', renderButton)
  }, [onSignedIn])

  return (
    <div className="mx-auto max-w-sm px-4 pt-24 text-center">
      <h1 className="text-lg font-semibold text-neutral-900">Sign in</h1>
      <p className="mt-1 text-sm text-neutral-500">
        Sign in with the Google account linked to this household.
      </p>
      {error && <Banner tone="over">{error}</Banner>}
      <div ref={buttonRef} className="mt-6 flex justify-center" />
    </div>
  )
}

function Home({
  state, loading, error, month, onMonth, onOpen, onAudit, onEvents, onOpenEvent, onLargePurchases,
  onReload, editMode, onToggleEditMode, pendingCaps, onChangeCap,
}) {
  const [events, setEvents] = useState(null)
  const [auditRequested, setAuditRequested] = useState(false)
  const [requestingAudit, setRequestingAudit] = useState(false)

  useEffect(() => {
    api.events().then((d) => setEvents(d.events)).catch(() => setEvents([]))
  }, [])

  async function requestAudit() {
    setRequestingAudit(true)
    try {
      await api.requestAudit()
      setAuditRequested(true)
    } catch {
      // A failed request here isn't worth a banner — the audit log will
      // simply show nothing new; the human can just tap it again.
    } finally {
      setRequestingAudit(false)
    }
  }

  if (loading && !state) return <Spinner label="Loading this month" />
  if (error) {
    return (
      <Banner tone="over">
        {error.message}
        <button onClick={onReload} className="ml-2 underline">
          retry
        </button>
      </Banner>
    )
  }
  if (!state) return null

  const { household, categories } = state
  const byState = (s) => categories.filter((c) => c.state === s)
  const red = byState('red')
  const orange = byState('orange')
  const yellow = byState('yellow')
  const green = byState('green')
  const uncapped = categories.filter(
    (c) => c.state === 'none' && c.category_id !== 'unreviewed' && c.kind !== 'excluded',
  )
  const unreviewed = categories.find((c) => c.category_id === 'unreviewed')

  const notices = []
  if (household.known_missing?.length > 0) {
    notices.push({
      tone: 'warn',
      text: `Expected but not seen this month: ${household.known_missing.join(', ')}. The surplus above does not include them.`,
    })
  }
  if (household.stale_accounts?.length > 0) {
    notices.push({ tone: 'info', text: `Accounts not syncing: ${household.stale_accounts.join(', ')}.` })
  }
  if (state.open_flags > 0) {
    notices.push({
      tone: 'info',
      text: `${state.open_flags} open flag${state.open_flags === 1 ? '' : 's'} — the next audit will pick ${state.open_flags === 1 ? 'it' : 'them'} up.`,
    })
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-sm font-medium text-neutral-500">{fmtMonth(month)}</h1>
        <div className="flex items-center gap-2">
          <select
            value={month ?? ''}
            onChange={(e) => onMonth(e.target.value)}
            className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs
                       text-neutral-600 shadow-sm"
          >
            {state.available_months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <NoticesBell notices={notices} />
          <button onClick={onEvents} className="text-xs font-medium text-brand-600">
            events
          </button>
          <button onClick={onAudit} className="text-xs font-medium text-brand-600">
            audit log
          </button>
          <button
            onClick={requestAudit}
            disabled={requestingAudit || auditRequested}
            className="text-xs font-medium text-brand-600 disabled:text-neutral-400"
            aria-label="Ask the console session to run an audit now"
          >
            {auditRequested ? 'audit requested' : 'run audit'}
          </button>
        </div>
      </header>

      <SurplusHeadline household={household} categories={categories} month={fmtMonth(month)} />

      <EditModeToggle editMode={editMode} onToggle={onToggleEditMode} />

      {editMode ? (
        <>
          <NewCategoryForm onCreated={onReload} />
          <PlanGrid categories={categories} pendingCaps={pendingCaps} onChangeCap={onChangeCap} />
        </>
      ) : (
        <>
          {unreviewed && Number(unreviewed.mtd) > 0 && (
            <button
              onClick={() => onOpen('unreviewed', 'Unreviewed')}
              className="w-full rounded-2xl border border-neutral-200 bg-white p-3.5 text-left shadow-sm"
            >
              <div className="flex justify-between">
                <span className="font-medium text-neutral-900">Unreviewed</span>
                <span className="tabular-nums text-neutral-900">{fmtMoney(unreviewed.mtd)}</span>
              </div>
              <p className="mt-1 text-xs text-neutral-400">
                Counted in the surplus, not yet categorized. Tap in to categorize it yourself, or
                wait for the next audit.
              </p>
            </button>
          )}

          <Group title="Over cap" items={red} onOpen={onOpen} />
          <Group title="Trending over" items={orange} onOpen={onOpen} />
          <Group title="Close to cap" items={yellow} onOpen={onOpen} />

          <EventsTracker events={events} onOpen={onOpenEvent} />

          {state.large_purchases > 0 && (
            <button
              onClick={onLargePurchases}
              className="w-full rounded-2xl border border-neutral-200 bg-white p-3.5 text-left shadow-sm"
            >
              <div className="flex justify-between">
                <span className="font-medium text-neutral-900">Large purchases to review</span>
                <span className="tabular-nums text-neutral-900">{state.large_purchases}</span>
              </div>
              <p className="mt-1 text-xs text-neutral-400">
                Big enough to ask: is this part of an event?
              </p>
            </button>
          )}

          <Collapsed title="Within cap" items={green} onOpen={onOpen} />
          <Collapsed title="No cap" items={uncapped} onOpen={onOpen} />
        </>
      )}
    </div>
  )
}

/**
 * Only events with a budget show up here — a budget is what makes an event
 * "a number to track" rather than just a label a few transactions happen to
 * share. Events without one still live on the full Events screen.
 */
function EventsTracker({ events, onOpen }) {
  const tracked = events?.filter((e) => e.budget != null) ?? []
  if (!tracked.length) return null

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Events</h2>
      {tracked.map((e) => {
        const over = e.remaining != null && e.remaining < 0
        return (
          <button
            key={e.event}
            onClick={() => onOpen(e.event)}
            className="flex w-full items-center justify-between rounded-2xl border border-neutral-200
                       bg-white p-3.5 text-left shadow-sm"
          >
            <span className="font-medium text-neutral-900">{e.event}</span>
            <span className="text-right">
              <span className="tabular-nums text-neutral-900">{fmtMoney(e.total)}</span>
              <span className={`ml-1 text-xs tabular-nums ${over ? 'text-over' : 'text-neutral-400'}`}>
                of {fmtMoney(e.budget)}
              </span>
            </span>
          </button>
        )
      })}
    </section>
  )
}

/**
 * Known-missing / stale-account / open-flag notices used to sit inline,
 * permanently claiming space even when nothing needed attention beyond a
 * glance. Now they're behind a badge, closed by default.
 */
function NoticesBell({ notices }) {
  const [open, setOpen] = useState(false)
  if (!notices.length) return null

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs
                   text-neutral-500 shadow-sm"
      >
        Notices
        <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full
                          bg-warn px-1 text-[10px] font-semibold text-white">
          {notices.length}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 space-y-2 rounded-2xl border
                         border-neutral-200 bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Notices
            </span>
            <button onClick={() => setOpen(false)} className="text-neutral-400" aria-label="Close">
              ✕
            </button>
          </div>
          {notices.map((n, i) => (
            <Banner key={i} tone={n.tone}>
              {n.text}
            </Banner>
          ))}
        </div>
      )}
    </div>
  )
}

function EditModeToggle({ editMode, onToggle }) {
  return (
    <label className="flex items-center justify-between rounded-2xl border border-neutral-200
                       bg-white px-3.5 py-2.5 text-sm shadow-sm">
      <span className="font-medium text-neutral-700">Plan targets</span>
      <button
        type="button"
        role="switch"
        aria-checked={editMode}
        onClick={onToggle}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          editMode ? 'bg-brand-500' : 'bg-neutral-200'
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            editMode ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  )
}

const CATEGORY_KINDS = ['discretionary', 'fixed', 'debt_payment', 'excluded']
const CATEGORY_OWNERS = ['shared', 'matt', 'lisa']

function slugify(label) {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

/**
 * A brand-new spending bucket — a rule can't reference a category that
 * doesn't exist yet (upsert_rule rejects an unknown one), so this has to
 * happen first. Writes immediately, unlike a cap edit's stage-then-Save
 * flow, since there's nothing to preview: either the category exists or it
 * doesn't. The id is derived from the label but stays editable, since two
 * categories can want similar-sounding labels.
 */
function NewCategoryForm({ onCreated }) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [id, setId] = useState('')
  const [idTouched, setIdTouched] = useState(false)
  const [kind, setKind] = useState('discretionary')
  const [owner, setOwner] = useState('shared')
  const [cap, setCap] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function changeLabel(value) {
    setLabel(value)
    if (!idTouched) setId(slugify(value))
  }

  async function submit(e) {
    e.preventDefault()
    if (!id.trim() || !label.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.createCategory(id.trim(), label.trim(), kind, owner, cap === '' ? null : Number(cap))
      setLabel('')
      setId('')
      setIdTouched(false)
      setCap('')
      setOpen(false)
      await onCreated()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl border border-dashed border-neutral-300 p-3 text-center
                   text-sm font-medium text-brand-600"
      >
        + New category
      </button>
    )
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-2 rounded-2xl border border-neutral-200 bg-white p-3.5 shadow-sm"
    >
      {error && <div className="text-xs text-over">{error.message}</div>}
      <input
        autoFocus
        value={label}
        onChange={(e) => changeLabel(e.target.value)}
        placeholder="Label (e.g. Board Games)"
        className="w-full rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm"
      />
      <div className="flex items-center gap-1.5 text-xs text-neutral-400">
        <span>id</span>
        <input
          value={id}
          onChange={(e) => {
            setId(e.target.value)
            setIdTouched(true)
          }}
          placeholder="board_games"
          className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-xs font-mono"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded-lg border border-neutral-200 px-2 py-1 text-sm text-neutral-700"
        >
          {CATEGORY_KINDS.map((k) => (
            <option key={k} value={k}>
              {k.replace('_', ' ')}
            </option>
          ))}
        </select>
        <select
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          className="rounded-lg border border-neutral-200 px-2 py-1 text-sm text-neutral-700"
        >
          {CATEGORY_OWNERS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {kind !== 'excluded' && (
          <input
            type="number"
            min="0"
            step="0.01"
            value={cap}
            onChange={(e) => setCap(e.target.value)}
            placeholder="Cap (optional)"
            className="w-28 rounded-lg border border-neutral-200 px-2 py-1 text-sm tabular-nums"
          />
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !label.trim() || !id.trim()}
          className="rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          Create
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full px-3 py-1 text-xs font-medium text-neutral-500"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

/**
 * Answers a different question than the surplus headline: not "how did the
 * month actually go" but "if every category lands right on its target, does
 * that even balance against take-home." Fixed to the top of the viewport
 * rather than inline — it needs to stay visible while dragging a slider
 * anywhere on the page, not just at the top of it. Recomputes live,
 * entirely client-side; nothing here is written until Save.
 */
function TargetBalanceBar({ household, categories, pendingCaps }) {
  const targetExpenses = categories.filter(isTargetable).reduce((sum, c) => {
    const effective = pendingCaps[c.category_id] ?? c.cap ?? c.projected_eom ?? 0
    return sum + Number(effective)
  }, 0)
  const takeHome = Number(household.take_home ?? 0)
  const balance = takeHome - targetExpenses
  const positive = balance >= 0

  return (
    <div className="fixed inset-x-0 top-0 z-20 border-b border-brand-200 bg-brand-50/95 backdrop-blur">
      <div className="mx-auto flex max-w-lg sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
        <span className="text-xs font-medium uppercase tracking-wide text-brand-700">Planning</span>
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className={`font-semibold tabular-nums ${positive ? 'text-ok' : 'text-over'}`}>
            {fmtMoney(Math.abs(balance))} {positive ? 'left' : 'over'}
          </span>
          <span className="text-neutral-300">·</span>
          <span className="tabular-nums text-neutral-500">
            {fmtMoney(takeHome)} − {fmtMoney(targetExpenses)}
          </span>
        </div>
      </div>
    </div>
  )
}

function Group({ title, items, onOpen }) {
  if (!items.length) return null
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{title}</h2>
      {items.map((c) => (
        <CategoryRow key={c.category_id} category={c} onOpen={onOpen} />
      ))}
    </section>
  )
}

function Collapsed({ title, items, onOpen }) {
  const [open, setOpen] = useState(false)
  if (!items.length) return null
  return (
    <section className="space-y-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-xs font-semibold uppercase
                   tracking-wide text-neutral-400"
      >
        <span>
          {title} ({items.length})
        </span>
        <span>{open ? '−' : '+'}</span>
      </button>
      {open && items.map((c) => <CategoryRow key={c.category_id} category={c} onOpen={onOpen} />)}
    </section>
  )
}

function Category({ categoryId, label, month, onBack, onFlagged, editMode, pendingCap, onChangeCap }) {
  const [data, setData] = useState(null)
  // null = not fetched yet — deferred until the one-time form is actually
  // opened, since every category view was otherwise paying for a
  // cross-category events scan nobody asked for.
  const [eventNames, setEventNames] = useState(null)
  // Same deferral as eventNames — most category views never open the flag
  // form, so most category views shouldn't pay for the full category list.
  const [categories, setCategories] = useState(null)
  const [rule, setRule] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Whether this category's own header stats (and the home screen's) are
  // now stale — checked once on the way out rather than re-fetching the
  // whole screen after every tap. See the same pattern on LargePurchases
  // for why: that redundant reload was most of what made one tap feel like
  // it took ten seconds.
  const dirty = useRef(false)
  const onFlaggedRef = useRef(onFlagged)
  onFlaggedRef.current = onFlagged

  const load = useCallback(async () => {
    try {
      setData(await api.category(categoryId, month))
    } catch (e) {
      setError(e)
    }
  }, [categoryId, month])

  const loadEventNames = useCallback(async () => {
    if (eventNames !== null) return
    try {
      const events = await api.events()
      setEventNames(events.events.map((e) => e.event))
    } catch (e) {
      setError(e)
    }
  }, [eventNames])

  const loadCategories = useCallback(async () => {
    if (categories !== null) return
    try {
      const result = await api.categories()
      setCategories(result.categories)
    } catch (e) {
      setError(e)
    }
  }, [categories])

  useEffect(() => {
    load()
  }, [load])

  // Unreviewed is the one screen where categorizing is the entire point of
  // being here, not an occasional detour — worth the category list on
  // every visit rather than waiting for the first tap to open the form.
  useEffect(() => {
    if (categoryId === 'unreviewed') loadCategories()
  }, [categoryId, loadCategories])

  // Runs on the way out however that happens — the back button or the
  // breadcrumb's home link both unmount this screen the same way.
  useEffect(() => () => {
    if (dirty.current) onFlaggedRef.current()
  }, [])

  async function flag(txn, category, note) {
    setBusy(true)
    try {
      await api.flag(txn.id, note, category)
      if (category) {
        // Recategorizing takes effect on the next categorize run — the
        // override written by /flags does nothing until this runs. This
        // is the one part of the sequence that's actually unavoidable.
        await api.recompute({ full: true })
      }
      // Optimistic rather than a full re-fetch of this screen (and the
      // home screen, via onFlagged): if it moved to a different category
      // it no longer belongs in this list; otherwise it's just flagged in
      // place. This screen's own header stats go stale until the next
      // visit rather than being re-fetched after every tap.
      setData((d) => (
        category && category !== categoryId
          ? { ...d, transactions: d.transactions.filter((t) => t.id !== txn.id) }
          : {
              ...d,
              transactions: d.transactions.map((t) => (t.id === txn.id ? { ...t, flagged: true } : t)),
            }
      ))
      dirty.current = true
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function split(txn, reimbursedAmount) {
    setBusy(true)
    try {
      await api.createSplit(txn.id, reimbursedAmount)
      await api.recompute({ full: true })
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function deleteSplit(splitId) {
    setBusy(true)
    try {
      await api.deleteSplit(splitId)
      await api.recompute({ full: true })
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function markOnetime(txn, event) {
    setBusy(true)
    try {
      await api.markOnetime(txn.id, event)
      await api.recompute({ full: true })
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function unmarkOnetime(onetimeId) {
    setBusy(true)
    try {
      await api.unmarkOnetime(onetimeId)
      await api.recompute({ full: true })
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (error) return <Banner tone="over">{error.message}</Banner>
  if (!data) return <Spinner />

  const meta = stateMeta(data.state?.state)

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm font-medium text-brand-600">
        ← back
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">
        {label ?? categoryId.replace(/_/g, ' ')}
      </h1>

      {data.state && (
        <div className="rounded-3xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-baseline justify-between">
            <span className={`text-sm font-medium ${meta.text}`}>{meta.label}</span>
            <span className="text-xs text-neutral-400">{data.month}</span>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div>
              <div className="text-xs text-neutral-400">Spent</div>
              <div className="tabular-nums text-neutral-900">{fmtMoney(data.state.mtd)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-400">Projected</div>
              <div className="tabular-nums text-neutral-900">{fmtMoney(data.state.projected_eom)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-400">Cap</div>
              <div className="tabular-nums text-neutral-900">
                {data.state.cap ? fmtMoney(data.state.cap) : '—'}
              </div>
            </div>
          </div>
          {editMode && categoryId !== 'unreviewed' && (
            <div className="mt-3">
              <TargetEditor
                categoryId={categoryId}
                cap={data.state.cap}
                fallback={data.state.projected_eom}
                pendingValue={pendingCap}
                onChange={onChangeCap}
              />
            </div>
          )}
          <div className="mt-4">
            <Sparkline history={data.history} cap={data.state.cap} />
          </div>
        </div>
      )}

      <TransactionsSection
        transactions={data.transactions}
        flagging={busy}
        onFlag={flag}
        onSplit={split}
        onDeleteSplit={deleteSplit}
        onMarkOnetime={markOnetime}
        onUnmarkOnetime={unmarkOnetime}
        eventNames={eventNames ?? []}
        onNeedEventNames={loadEventNames}
        categories={categories ?? []}
        onNeedCategories={loadCategories}
        onOpenRule={async (id) => setRule(await api.rule(id))}
        showOnetime={categoryId !== 'unreviewed'}
        categorizeMode={categoryId === 'unreviewed'}
        startOpen={categoryId === 'unreviewed'}
      />

      {rule && <RuleSheet rule={rule} onClose={() => setRule(null)} />}
    </div>
  )
}

function TransactionsSection({
  transactions, flagging, onFlag, onSplit, onDeleteSplit, onMarkOnetime, onUnmarkOnetime,
  eventNames, onNeedEventNames, categories, onNeedCategories, onOpenRule, showOnetime = true,
  categorizeMode = false, startOpen = false,
}) {
  const [open, setOpen] = useState(startOpen)
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-neutral-700"
      >
        <span>Transactions ({transactions.length})</span>
        <span className="text-neutral-400">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <ul className="border-t border-neutral-100 px-4">
          {transactions.map((t) => (
            <TransactionRow
              key={t.id}
              txn={t}
              flagging={flagging}
              splitting={flagging}
              markingOnetime={flagging}
              onFlag={onFlag}
              onSplit={onSplit}
              onDeleteSplit={onDeleteSplit}
              onMarkOnetime={onMarkOnetime}
              onUnmarkOnetime={onUnmarkOnetime}
              eventNames={eventNames}
              onNeedEventNames={onNeedEventNames}
              categories={categories}
              onNeedCategories={onNeedCategories}
              onOpenRule={onOpenRule}
              showOnetime={showOnetime}
              categorizeMode={categorizeMode}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function RuleSheet({ rule, onClose }) {
  return (
    <div
      className="fixed inset-0 z-10 flex items-end bg-neutral-900/40"
      onClick={onClose}
      role="dialog"
    >
      <div
        className="max-h-[70vh] w-full overflow-auto rounded-t-3xl bg-white p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between">
          <h2 className="font-mono text-sm text-neutral-900">{rule.id}</h2>
          <button onClick={onClose} className="text-neutral-400">
            ✕
          </button>
        </div>
        {rule.synthetic ? (
          <p className="mt-3 text-sm text-neutral-600">{rule.explanation}</p>
        ) : (
          <dl className="mt-3 space-y-2 text-sm">
            <Field label="Category" value={rule.category} />
            <Field label="Matches" value={matchSummary(rule)} />
            {rule.owner && <Field label="Owner" value={rule.owner} />}
            <Field label="Source" value={`${rule.source} · confidence ${rule.confidence}`} />
            {rule.created_by && <Field label="Created by" value={rule.created_by} />}
            {rule.evidence && <Field label="Evidence" value={rule.evidence} />}
            {rule.note && <Field label="Note" value={rule.note} />}
            <Field label="Currently catches" value={`${rule.txn_count} transactions`} />
          </dl>
        )}
      </div>
    </div>
  )
}

function matchSummary(rule) {
  return (
    [
      rule.merchant && `merchant = ${rule.merchant}`,
      rule.merchant_regex && `merchant ~ ${rule.merchant_regex}`,
      rule.statement_regex && `statement ~ ${rule.statement_regex}`,
      rule.account && `account = ${rule.account}`,
      rule.override_exclusion && 'overrides Monarch exclusion',
    ]
      .filter(Boolean)
      .join('; ') || '—'
  )
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-neutral-400">{label}</dt>
      <dd className="break-words text-neutral-800">{value}</dd>
    </div>
  )
}

function AuditLog({ onBack }) {
  const [records, setRecords] = useState(null)
  useEffect(() => {
    api.auditLog().then((d) => setRecords(d.records))
  }, [])

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm font-medium text-brand-600">
        ← back
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">Audit log</h1>
      {!records && <Spinner />}
      {records?.length === 0 && <p className="text-sm text-neutral-500">No audits recorded yet.</p>}
      {records?.map((r, i) => (
        <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-3.5 shadow-sm">
          <div className="text-xs text-neutral-400">{String(r.run_at).slice(0, 16)}</div>
          <div className="mt-1 text-sm text-neutral-800">{r.summary}</div>
          {r.modified_manual_rules?.length > 0 && (
            <div className="mt-2 text-xs font-medium text-warn">
              Modified manual rules: {r.modified_manual_rules.join(', ')}
            </div>
          )}
          {r.changes?.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-neutral-400">
              {r.changes.map((c, j) => (
                <li key={j}>{c.reason}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * "How much did the Tahoe trip actually cost" — a cross-category total for
 * whatever transactions someone tagged one-time with the same event name.
 * Exists only because of POST /onetime; there's no separate events table.
 */
function Events({ onBack, onOpen }) {
  const [events, setEvents] = useState(null)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [budget, setBudget] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api.events().then((d) => setEvents(d.events)).catch(setError)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function createEvent(e) {
    e.preventDefault()
    const trimmed = name.trim()
    const num = Number(budget)
    if (!trimmed || !Number.isFinite(num) || num < 0) return
    setBusy(true)
    setError(null)
    try {
      await api.createEvent(trimmed, num)
      setName('')
      setBudget('')
      setCreating(false)
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm font-medium text-brand-600">
        ← back
      </button>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-900">Events</h1>
        {!creating && (
          <button onClick={() => setCreating(true)} className="text-sm font-medium text-brand-600">
            + new event
          </button>
        )}
      </div>
      {creating && (
        <form
          onSubmit={createEvent}
          className="space-y-2 rounded-2xl border border-neutral-200 bg-white p-3.5 shadow-sm"
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Event name (e.g. Tahoe trip)"
            className="w-full rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="Budget"
            className="w-full rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm tabular-nums"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => { setCreating(false); setError(null) }}
              className="rounded-full px-3 py-1 text-xs font-medium text-neutral-500"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {error && <Banner tone="over">{error.message}</Banner>}
      {!events && !error && <Spinner />}
      {events?.length === 0 && !creating && (
        <p className="text-sm text-neutral-500">
          No events yet — create one with a budget, or mark a transaction "One-time" and give it an
          event name.
        </p>
      )}
      {events?.map((e) => {
        const over = e.budget != null && e.remaining < 0
        return (
          <button
            key={e.event}
            onClick={() => onOpen(e.event)}
            className="flex w-full items-center justify-between rounded-2xl border border-neutral-200
                       bg-white p-3.5 text-left shadow-sm"
          >
            <div>
              <div className="font-medium text-neutral-900">{e.event}</div>
              <div className="text-xs text-neutral-400">
                {e.transaction_count} transaction{e.transaction_count === 1 ? '' : 's'}
              </div>
            </div>
            <div className="text-right">
              <div className="tabular-nums text-neutral-900">{fmtMoney(e.total)}</div>
              {e.budget != null && (
                <div className={`text-xs tabular-nums ${over ? 'text-over' : 'text-neutral-400'}`}>
                  of {fmtMoney(e.budget)} budget
                </div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function EventDetail({ event, onBack }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [editingBudget, setEditingBudget] = useState(false)
  const [budgetInput, setBudgetInput] = useState('')

  const load = useCallback(async () => {
    try {
      const fresh = await api.eventTransactions(event)
      setData(fresh)
      setBudgetInput(fresh.budget != null ? String(fresh.budget) : '')
    } catch (e) {
      setError(e)
    }
  }, [event])

  useEffect(() => {
    load()
  }, [load])

  async function unmarkOnetime(onetimeId) {
    setBusy(true)
    try {
      await api.unmarkOnetime(onetimeId)
      await api.recompute({ full: true })
      const fresh = await api.eventTransactions(event).catch(() => null)
      if (!fresh || fresh.transactions.length === 0) {
        onBack()
        return
      }
      setData(fresh)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function saveBudget(e) {
    e.preventDefault()
    const num = Number(budgetInput)
    if (!Number.isFinite(num) || num < 0) return
    setBusy(true)
    try {
      await api.setEventBudget(event, num)
      await load()
      setEditingBudget(false)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function clearBudget() {
    setBusy(true)
    try {
      await api.deleteEventBudget(event)
      await load()
      setEditingBudget(false)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (error) return <Banner tone="over">{error.message}</Banner>
  if (!data) return <Spinner />

  const over = data.budget != null && data.remaining < 0

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm font-medium text-brand-600">
        ← back
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">{event}</h1>

      <div className="rounded-3xl border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs text-neutral-400">Total</div>
            <div className="text-2xl font-semibold tabular-nums text-neutral-900">
              {fmtMoney(data.total)}
            </div>
          </div>
          {data.budget != null && (
            <div className="text-right">
              <div className="text-xs text-neutral-400">of {fmtMoney(data.budget)} budget</div>
              <div className={`text-sm font-medium tabular-nums ${over ? 'text-over' : 'text-ok'}`}>
                {over ? `${fmtMoney(-data.remaining)} over` : `${fmtMoney(data.remaining)} left`}
              </div>
            </div>
          )}
        </div>
        <div className="text-xs text-neutral-400">
          {data.transaction_count} transaction{data.transaction_count === 1 ? '' : 's'}
        </div>

        {data.budget != null && (
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className={`h-full rounded-full ${over ? 'bg-over' : 'bg-brand-500'}`}
              style={{ width: `${Math.min(100, (data.total / data.budget) * 100)}%` }}
            />
          </div>
        )}

        {editingBudget ? (
          <form onSubmit={saveBudget} className="mt-3 flex items-center gap-2">
            <span className="text-xs text-neutral-400">Budget $</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              autoFocus
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              className="w-24 rounded-lg border border-neutral-200 px-2 py-1 text-sm tabular-nums"
            />
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
            >
              Save
            </button>
            {data.budget != null && (
              <button
                type="button"
                onClick={clearBudget}
                disabled={busy}
                className="text-xs text-neutral-400 underline disabled:opacity-40"
              >
                Clear
              </button>
            )}
          </form>
        ) : (
          <button
            onClick={() => setEditingBudget(true)}
            className="mt-3 text-xs font-medium text-brand-600"
          >
            {data.budget != null ? 'Edit budget' : 'Set a budget'}
          </button>
        )}
      </div>

      <ul className="rounded-3xl border border-neutral-200 bg-white px-4 shadow-sm">
        {data.transactions.map((t) => (
          <TransactionRow
            key={t.id}
            txn={t}
            categoryLabel={t.category_label}
            markingOnetime={busy}
            onUnmarkOnetime={unmarkOnetime}
            onOpenRule={() => {}}
            showSplit={false}
            showFlag={false}
          />
        ))}
      </ul>
    </div>
  )
}

/**
 * Open questions, not alerts — a big charge nobody's said yes or no to yet.
 * The choice is binary: assign it to an event (its own separate budget) or
 * say it's regular spending — there's no third "one-time but not part of
 * anything" option here, unlike the general per-transaction one-time toggle
 * elsewhere in the app. See common/large_purchases.py for why fixed and
 * debt-payment categories never show up here.
 */
function LargePurchases({ onBack, onReviewed }) {
  const [data, setData] = useState(null)
  const [eventNames, setEventNames] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Whether the home screen's own state (its large-purchase badge count,
  // mainly) is now stale — set on any change here, checked once on the way
  // back out rather than re-fetching /state after every single tap, which
  // was most of why one tap felt like it took ten seconds.
  const dirty = useRef(false)
  // Read through a ref (not the effect's dependency array) so the
  // mount/unmount effect below only ever fires on a real unmount — App
  // passes a new `onReviewed` closure every render, and depending on it
  // directly would re-fire the "leaving" cleanup on every unrelated
  // re-render instead of just once on the way out.
  const onReviewedRef = useRef(onReviewed)
  onReviewedRef.current = onReviewed

  const load = useCallback(async () => {
    try {
      const [large, events] = await Promise.all([api.largePurchases(), api.events()])
      setData(large)
      setEventNames(events.events.map((e) => e.event))
    } catch (e) {
      setError(e)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Runs on the way out however that happens — the back button or the
  // breadcrumb's home link both unmount this screen the same way.
  useEffect(() => () => {
    if (dirty.current) onReviewedRef.current()
  }, [])

  async function markOnetime(txn, event) {
    setBusy(true)
    try {
      await api.markOnetime(txn.id, event)
      await api.recompute({ full: true })
      // Optimistic: this transaction now has a onetime/event record, so it
      // no longer qualifies as an open large-purchase candidate — no need
      // to re-fetch the whole list just to see it drop off. A brand-new
      // event name typed here (rather than picked from the dropdown) gets
      // appended locally too, so it's there if the form reopens.
      setData((d) => ({
        ...d,
        transaction_count: d.transaction_count - 1,
        transactions: d.transactions.filter((t) => t.id !== txn.id),
      }))
      if (event) setEventNames((names) => (names.includes(event) ? names : [...names, event]))
      dirty.current = true
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function dismiss(txn) {
    setBusy(true)
    try {
      await api.dismissLargePurchase(txn.id)
      setData((d) => ({
        ...d,
        transaction_count: d.transaction_count - 1,
        transactions: d.transactions.filter((t) => t.id !== txn.id),
      }))
      dirty.current = true
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (error) return <Banner tone="over">{error.message}</Banner>
  if (!data) return <Spinner />

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm font-medium text-brand-600">
        ← back
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">Large purchases</h1>
      <p className="text-sm text-neutral-500">
        Big enough to ask: is this part of an event, or just regular monthly spending?
      </p>
      {data.transactions.length === 0 && (
        <p className="text-sm text-neutral-500">Nothing to review right now.</p>
      )}
      {data.transactions.length > 0 && (
        <ul className="rounded-3xl border border-neutral-200 bg-white px-4 shadow-sm">
          {data.transactions.map((t) => (
            <TransactionRow
              key={t.id}
              txn={t}
              categoryLabel={t.category_label}
              eventNames={eventNames}
              markingOnetime={busy}
              dismissingLarge={busy}
              onMarkOnetime={markOnetime}
              onDismissLarge={dismiss}
              onOpenRule={() => {}}
              eventOnly
              showSplit={false}
              showFlag={false}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
